import { useEffect, useState } from "react";
import { Download, Share2, Plus, X } from "lucide-react";
import { Button } from "../components/ui/button";

/**
 * Install-prompt banner.
 *
 * Two flows depending on the platform:
 *
 *   • Chromium (Android / desktop):
 *     We capture the `beforeinstallprompt` event, suppress the
 *     browser's default mini-bar, and surface our own banner. On
 *     "התקן" we call `prompt()` and resolve the user choice.
 *
 *   • Safari iOS:
 *     Apple doesn't expose `beforeinstallprompt`. We sniff
 *     `iPad|iPhone|iPod` + standalone-mode === false, and show
 *     a step-by-step "Share → Add to Home Screen" card.
 *
 * Dismissal rules:
 *   - Tap "אולי אחר כך" → suppressed for 7 days.
 *   - Successful install (or `appinstalled` event) → suppressed
 *     forever.
 *   - Already running standalone (`display-mode: standalone`) →
 *     never shown.
 */

const LS_DISMISSED_AT = "torahya_pwa_dismissed_at";
const LS_INSTALLED     = "torahya_pwa_installed";
const SEVEN_DAYS_MS    = 7 * 24 * 60 * 60 * 1000;

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports as Mac; the touch-points sniff is the
    // standard workaround.
    (ua.includes("Mac") && navigator.maxTouchPoints > 1)
  );
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari (pre-13.2) exposed this non-standard flag.
    window.navigator.standalone === true
  );
}

function shouldShow() {
  if (typeof window === "undefined") return false;
  if (isStandalone()) return false;
  try {
    if (localStorage.getItem(LS_INSTALLED) === "1") return false;
    const dismissed = localStorage.getItem(LS_DISMISSED_AT);
    if (dismissed && Date.now() - Number(dismissed) < SEVEN_DAYS_MS) return false;
  } catch {}
  return true;
}

export default function InstallPrompt() {
  // null = nothing to show.  "android" = Chromium prompt captured.
  // "ios" = Safari iOS, needs manual instructions.
  const [mode, setMode] = useState(null);
  const [deferred, setDeferred] = useState(null);

  useEffect(() => {
    if (!shouldShow()) return;

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Tiny delay so the banner doesn't punch in during initial
      // page render.
      setTimeout(() => setMode("android"), 1200);
    };

    const onInstalled = () => {
      try { localStorage.setItem(LS_INSTALLED, "1"); } catch {}
      setMode(null);
      setDeferred(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // iOS: no `beforeinstallprompt`, so we surface the manual flow
    // ourselves after a short delay (gives the user time to land).
    if (isIOS()) {
      const t = setTimeout(() => setMode((curr) => curr ?? "ios"), 2500);
      return () => {
        window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
        window.removeEventListener("appinstalled", onInstalled);
        clearTimeout(t);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(LS_DISMISSED_AT, String(Date.now())); } catch {}
    setMode(null);
  };

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") {
        try { localStorage.setItem(LS_INSTALLED, "1"); } catch {}
      } else {
        // Treat declines the same as "later" — 7-day silence.
        try { localStorage.setItem(LS_DISMISSED_AT, String(Date.now())); } catch {}
      }
    } catch (err) {
      console.warn("[PWA] install prompt failed:", err);
    } finally {
      setDeferred(null);
      setMode(null);
    }
  };

  if (!mode) return null;

  return (
    <div
      dir="rtl"
      data-testid="pwa-install-banner"
      className="fixed inset-x-3 bottom-3 z-50 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:max-w-sm"
    >
      <div className="relative rounded-xl border border-gold/30 bg-[#F5EFE0] text-[#1E2A3A] shadow-2xl shadow-black/20 px-4 py-3.5">
        <button
          onClick={dismiss}
          aria-label="סגור"
          data-testid="pwa-install-close"
          className="absolute top-2 left-2 p-1 rounded-full text-[#1E2A3A]/50 hover:text-[#1E2A3A]"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.8} />
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="shrink-0 h-11 w-11 rounded-lg bg-[#1E3A5F] grid place-items-center overflow-hidden">
            <img
              src="/brand/icon-192.png"
              alt="תוריא"
              className="h-9 w-9 object-contain"
            />
          </div>
          <div className="min-w-0">
            <div className="font-heading text-base text-[#1E3A5F] leading-tight">
              התקן את תוריא
            </div>
            <div className="text-[11px] text-[#1E2A3A]/70 leading-tight mt-0.5">
              גישה מהירה מהמסך הראשי, ללא דפדפן.
            </div>
          </div>
        </div>

        {mode === "android" ? (
          <div className="flex gap-2 mt-2 justify-end">
            <button
              onClick={dismiss}
              data-testid="pwa-install-later"
              className="text-xs px-3 py-1.5 rounded-full text-[#1E2A3A]/70 hover:bg-[#1E2A3A]/5"
            >
              אולי אחר כך
            </button>
            <Button
              onClick={install}
              data-testid="pwa-install-accept"
              size="sm"
              className="rounded-full bg-[#1E3A5F] hover:bg-[#2a4d78] text-[#F5EFE0] gap-1.5"
            >
              <Download className="h-3.5 w-3.5" strokeWidth={1.6} />
              התקן
            </Button>
          </div>
        ) : (
          // iOS — manual instructions
          <div className="mt-2 text-[12px] text-[#1E2A3A]/85 space-y-1.5">
            <div className="flex items-center gap-1.5">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#1E3A5F]/10 text-[10px] font-mono text-[#1E3A5F]">1</span>
              לחץ על
              <Share2 className="h-3.5 w-3.5 text-[#1E3A5F]" strokeWidth={1.6} />
              בסרגל הדפדפן
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#1E3A5F]/10 text-[10px] font-mono text-[#1E3A5F]">2</span>
              בחר
              <Plus className="h-3.5 w-3.5 text-[#1E3A5F]" strokeWidth={1.6} />
              "הוסף למסך הבית"
            </div>
            <div className="flex justify-end pt-1">
              <button
                onClick={dismiss}
                data-testid="pwa-install-later"
                className="text-[11px] px-3 py-1.5 rounded-full text-[#1E2A3A]/70 hover:bg-[#1E2A3A]/5"
              >
                הבנתי
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
